import React, { useState } from 'react';
import { IMessage } from '../../services/messaging/IMessagingProvider';
import { MessageMedia } from './MessageMedia';

interface MessageBubbleProps {
  message: IMessage;
  isOwn: boolean;
  onDelete?: (messageId: string) => void;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({ message, isOwn, onDelete }) => {
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const formatTimestamp = (timestamp: number | string | Date) => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
        ' ' + date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    }
  };

  // Check if message is within 5-minute delete window
  const messageAge = Date.now() - (typeof message.timestamp === 'number' ? message.timestamp : new Date(message.timestamp).getTime());
  const canDelete = isOwn && onDelete && messageAge < 5 * 60 * 1000;

  const handleDelete = async () => {
    if (!onDelete || deleting) return;

    const confirmed = window.confirm('Delete this message? This action cannot be undone.');
    if (!confirmed) return;

    setDeleting(true);
    try {
      await onDelete(message.id);
    } catch (error: any) {
      alert(error.message || 'Failed to delete message');
      setDeleting(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: isOwn ? 'flex-end' : 'flex-start',
        marginBottom: '8px',
        position: 'relative'
      }}
      onMouseEnter={() => canDelete && setShowDelete(true)}
      onMouseLeave={() => setShowDelete(false)}
    >
      {canDelete && showDelete && (
        <button
          onClick={handleDelete}
          disabled={deleting}
          style={{
            position: 'absolute',
            right: isOwn ? 'calc(70% + 8px)' : 'auto',
            left: isOwn ? 'auto' : 'calc(70% + 8px)',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '28px',
            height: '28px',
            cursor: deleting ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: deleting ? 0.5 : 1,
            zIndex: 10,
            fontSize: '16px',
            padding: 0
          }}
          title={`Delete (${Math.ceil((5 * 60 * 1000 - messageAge) / 1000)}s left)`}
        >
          {deleting ? '...' : '×'}
        </button>
      )}
      <div
        style={{
          maxWidth: '70%',
          padding: '10px 14px',
          borderRadius: '18px',
          backgroundColor: isOwn ? 'var(--primary-color)' : 'var(--background-paper)',
          color: isOwn ? 'white' : 'var(--text-primary)',
          border: isOwn ? 'none' : '1px solid var(--border-color)',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
          opacity: deleting ? 0.5 : 1
        }}
      >
        {message.content && (
          <div style={{ marginBottom: message.mediaUrl ? '8px' : '0' }}>
            {message.content}
          </div>
        )}

        {message.mediaUrl && (
          <MessageMedia
            mediaType={message.mediaType as 'image' | 'video' | 'link'}
            mediaUrl={message.mediaUrl}
            thumbnailUrl={message.thumbnailUrl}
          />
        )}

        <div
          style={{
            fontSize: '11px',
            marginTop: '4px',
            opacity: 0.7,
            textAlign: 'right',
            color: isOwn ? 'rgba(255, 255, 255, 0.8)' : 'var(--text-secondary)'
          }}
        >
          {formatTimestamp(message.timestamp)}
          {isOwn && message.status === 'read' && (
            <span style={{ marginLeft: '4px' }}>
              <span className="material-icons" style={{ fontSize: '12px', verticalAlign: 'middle' }}>
                done_all
              </span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
